import { useEffect, useState } from "react";
import {
  Button,
  MenuItem,
  CircularProgress,
  Grid,
  InputLabel,
  FormControl,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Select from "@mui/material/Select";
import { post, put } from "src/utils/networkUtils";
import useTenantCompetitions from "src/hooks/useTenantCompetitions";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { getEnabledLangs } from "src/langs/langs";
import LanguageTabs from "src/components/language/LanguageTabs";

const dateFormat = "YYYY-MM-DD HH:mm:ss";
const LANGS = getEnabledLangs();

const defaultStage = {
  name: "",
  nameI18n: { en: "", fr: "" },
  identifier: "",
  startDate: moment().format(dateFormat),
  endDate: moment().format(dateFormat),
  breakdown: "",
  isCompleted: false,
  prizePool: "",
};
export default function StageDialog(props) {
  const [stage, setStage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedLanguageTab, setSelectedLanguageTab] = useState(() => {
    const enIndex = LANGS.findIndex((l) => l.value === "en");
    return enIndex >= 0 ? enIndex : 0;
  });

  // Use the hook to get tenant competitions
  const { tenantCompetitions } = useTenantCompetitions(props.tenantId);

  useEffect(() => {
    if (props?.stage) {
      setStage(props.stage);
    } else {
      setStage(defaultStage);
    }

    // Set default competition if tenant competitions are available
    if (tenantCompetitions.length > 0) {
      setStage((prevState) => ({
        ...prevState,
        competitionId: tenantCompetitions[0].competitionId,
      }));
    }
  }, [props.stage, tenantCompetitions]);
  
  const handleChange = (e) => {
    const { id, value } = e.target;

    setStage((prev) => {
      // Handle all i18n fields dynamically: "nameI18n.en", "nameI18n.fr", etc.
      if (id.includes(".")) {
        const [field, lang] = id.split(".");
        const isPrimaryLang = lang === LANGS[0].value;
        return {
          ...prev,
          [field]: { ...(prev[field] || {}), [lang]: value },
          // Keep legacy flat "name" in sync with the primary language
          ...(isPrimaryLang && field === "nameI18n" && { name: value }),
        };
      }

      return { ...prev, [id]: value };
    });
  };

  const onSuccess = () => {
    setLoading(true);
    stage.tenantId = parseInt(props.tenantId) || 1;
    if (props?.stage) {
      put(`/admin/stages/${stage.stageId}?tenantId=${props.tenantId}`, stage).then(function () {
        props.onSuccessHandler();
      });
    } else {
      post(`/admin/stages?tenantId=${props.tenantId}`, stage).then(function () {
        props.onSuccessHandler();
      });
    }
  };
  const onCancel = () => {
    props.onClose();
  };

  const handleDateChange = (newValue, fieldName) => {
    setStage((prevState) => ({
      ...prevState,
      [fieldName]: moment(newValue).seconds(0).format(dateFormat),
    }));
  };
  const handleCompetitionIdChange = (value) => {
    setStage((prevState) => ({
      ...prevState,
      competitionId: value,
    }));
  };
  const handleChangeCompleted = (e) => {
    setStage((prevState) => ({
      ...prevState,
      isCompleted: e.target.checked,
    }));
  };

  return (
    <Dialog
      open={props.open}
      onClose={() => { }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Stage</DialogTitle>
      <DialogContent>
        {stage === null || loading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center" }}
            style={{ padding: 20 }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Grid container>
            <Grid item xs={12} sx={{ m: 0, p: 1 }}>
              <LanguageTabs
                langs={LANGS}
                value={selectedLanguageTab}
                onChange={(_, newValue) => setSelectedLanguageTab(newValue)}
                ariaLabel="stage language tabs"
                idPrefix="stage-language-tab"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              />
            </Grid>
            <Grid item xs={4} sx={{ m: 0, p: 1 }}>
              <TextField
                id={`nameI18n.${LANGS[selectedLanguageTab]?.value}`}
                label={`Name (${LANGS[selectedLanguageTab]?.label})`}
                fullWidth
                value={stage.nameI18n?.[LANGS[selectedLanguageTab]?.value] ?? ""}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={4} sx={{ m: 0, p: 1 }}>
              <TextField
                id="identifier"
                label="Identifier"
                fullWidth
                value={stage.identifier}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={4} sx={{ m: 0, p: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stage.isCompleted}
                    onChange={handleChangeCompleted}
                    id="isCompleted"
                  />
                }
                label="Completed"
              />
            </Grid>
            <Grid item xs={6} sx={{ m: 0, p: 1 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="Start Date"
                  value={moment(stage.startDate, dateFormat)}
                  onChange={(value) => handleDateChange(value, "startDate")}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} sx={{ m: 0, p: 1 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="End Date"
                  value={moment(stage.endDate, dateFormat)}
                  onChange={(value) => handleDateChange(value, "endDate")}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sx={{ m: 0, p: 1 }}>
              <TextField
                id="breakdown"
                label="Breakdown"
                fullWidth
                value={stage.breakdown}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={4} sx={{ m: 0, p: 1 }}>
              <TextField
                id="prizePool"
                label="Prize Pool"
                fullWidth
                value={stage.prizePool}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sx={{ m: 0, p: 1 }}>
              <TextField
                id="customPrizeBreakdown"
                label="Custom Prize Breakdown"
                fullWidth
                value={stage.customPrizeBreakdown}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={4} sx={{ m: 0, p: 1 }}>
              <TextField
                id="customPrizePool"
                label="Custom Prize Pool"
                fullWidth
                value={stage.customPrizePool}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={4} sx={{ m: 0, p: 1 }}>
              <TextField
                id="entryFee"
                label="Entry Fee Coins"
                fullWidth
                value={stage.entryFee}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={4} sx={{ m: 0, p: 1 }}>
              <FormControl sx={{ m: 1, minWidth: 150 }} size="small">
                <InputLabel id="demo-simple-select-label">
                  Competition
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={stage.competitionId ?? ""}
                  label="Competition"
                  onChange={(event) =>
                    handleCompetitionIdChange(event.target.value)
                  }
                >
                  {tenantCompetitions.length > 0 &&
                    tenantCompetitions.map((tenantCompetition) => (
                      <MenuItem
                        value={tenantCompetition.competitionId}
                        key={`competition-${tenantCompetition.competitionId}`}
                      >
                        {tenantCompetition.competitionName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onCancel()}>Cancel</Button>
        <Button onClick={() => onSuccess()} autoFocus disabled={false}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
